import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Button, Input, Tag, Spin } from "antd";
import { withTranslation } from "react-i18next";
import moment from "moment";
import Colors from "../themes/Colors";

const { TextArea } = Input;

const SEPARATOR_REGEX = /[\s,;.\t\n\r]+/;

const REASON_KEYS = {
  not_found: "ID not found",
  archived: "Screen archived",
  secondary_display: "Secondary screen",
  not_for_sale: "Screen not for sale",
  min_campaign_time_not_met: "Campaign time too short",
  not_available_for_programmatic: "Not available for programmatic",
};

const AddDisplaysByIdModal = ({
  t,
  visible,
  onCancel,
  loading,
  result,
  user,
  setShouldPromptLogin,
  checkDisplaysByIds,
  date_from,
  date_to,
  editCampaignActive,
  currency,
  campaignCurrency,
  invoiceIssuingCountry,
  campaignInvoiceIssuingCountry,
  client,
  programmatic,
  dsp,
  isGuaranteed,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [localResult, setLocalResult] = useState(null);
  const [step, setStep] = useState(1);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setInputValue("");
      setLocalResult(null);
      setStep(1);
      submittedRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (submittedRef.current && !loading && result) {
      setLocalResult(result);
      submittedRef.current = false;
    }
  }, [loading, result]);

  useEffect(() => {
    if (localResult) {
      setStep(2);
    }
  }, [localResult]);

  const parsedIds = useMemo(() => {
    if (!inputValue.trim()) return [];
    const tokens = inputValue.split(SEPARATOR_REGEX);
    const ids = tokens.map(token => token.trim()).filter(token => /^\d+$/.test(token));
    return [...new Set(ids)];
  }, [inputValue]);

  const handleConfirm = () => {
    if (!user) {
      setShouldPromptLogin(true, null);
      return;
    }
    setLocalResult(null);
    submittedRef.current = true;
    checkDisplaysByIds({
      display_ids: parsedIds.map(Number),
      date_from: moment(date_from || new Date()).format("YYYY-MM-DD"),
      date_to: moment(date_to || new Date()).format("YYYY-MM-DD"),
      smart_campaign_enabled: programmatic === "programmatic" ? 1 : 0,
      currency: editCampaignActive ? campaignCurrency : currency,
      invoice_issuing_country: editCampaignActive
        ? campaignInvoiceIssuingCountry
        : invoiceIssuingCountry,
      client_id: client ? client.id : null,
      is_guaranteed: isGuaranteed ? 1 : 0,
      external_dsp_id: dsp ? dsp.id : null,
    });
  };

  const rejectedItems = useMemo(() => {
    if (!localResult) return [];
    const items = [];
    (localResult.invalid || []).forEach(item => {
      items.push({
        id: item.id,
        reason: t(REASON_KEYS[item.reason] || item.reason),
      });
    });
    (localResult.alreadyInCartIds || []).forEach(id => {
      items.push({ id, reason: t("Already added to campaign") });
    });
    return items;
  }, [localResult, t]);

  const addedIds = localResult ? (localResult.addedIds || []) : [];
  const addedCount = localResult ? (localResult.added || 0) : 0;
  const rejectedCount = rejectedItems.length;

  const footer = step === 1
    ? [
        <Button key="cancel" onClick={onCancel}>{t("Cancel")}</Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={parsedIds.length === 0 || loading}
          loading={loading}
        >
          {t("Add")}
        </Button>,
      ]
    : [
        <Button key="continue" type="primary" onClick={onCancel}>
          {t("Continue")}
        </Button>,
      ];

  return (
    <Modal
      title={
        <span style={{ color: Colors.primaryAccent, fontWeight: 600 }}>
          {t("Add screens by ID")}
        </span>
      }
      visible={visible}
      onCancel={onCancel}
      footer={footer}
      width={520}
      bodyStyle={{ padding: "16px 24px" }}
    >
      {step === 1 ? (
        <Spin spinning={loading}>
          <TextArea
            rows={5}
            placeholder={t("Screen IDs placeholder")}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            style={{ borderColor: Colors.primaryAccent, borderRadius: 8, resize: "vertical" }}
          />

          {parsedIds.length > 0 && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                maxHeight: 120,
                overflowY: "auto",
                padding: "8px",
                background: Colors.extraLightPrimary,
                borderRadius: 8,
              }}
            >
              {parsedIds.map(id => (
                <Tag
                  key={id}
                  color={Colors.primary}
                  style={{ margin: 0, borderRadius: 12, fontSize: 12 }}
                >
                  {id}
                </Tag>
              ))}
            </div>
          )}

          <div style={{ marginTop: 10, color: Colors.coal, fontWeight: 500 }}>
            {t("Total screens")}: <strong>{parsedIds.length}</strong>
          </div>
        </Spin>
      ) : (
        <div>
          {addedCount > 0 && (
            <div
              style={{
                background: Colors.extraLightPrimary,
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: rejectedCount > 0 ? 12 : 0,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                {t("screens added summary", { count: addedCount })}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {addedIds.map(id => (
                  <Tag
                    key={id}
                    color={Colors.primary}
                    style={{ margin: 0, borderRadius: 12, fontSize: 12 }}
                  >
                    {id}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {rejectedCount > 0 && (
            <div
              style={{
                background: Colors.tintError,
                border: "1px solid #ffa39e",
                borderRadius: 8,
                padding: "12px 16px",
              }}
            >
              <div style={{ fontWeight: 600, color: "#cf1322", marginBottom: 8 }}>
                {t("screens rejected summary", { count: rejectedCount })}
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {rejectedItems.map((item, idx) => (
                  <li key={idx} style={{ fontSize: 13 }}>
                    <strong>{item.id}</strong>{" - "}{item.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default withTranslation()(AddDisplaysByIdModal);
